import { ClientDialog } from '@/components/common/modal/ClientDialog';
import { DialogSingleAction, getDialogContentPaddingBottom } from '@/components/common/modal/DialogSingleAction';
import { PHONE_BINDING_NOTICE_TITLE_ID, PhoneBindingNotice } from '@/components/common/phone/PhoneBindingNotice';
import { PhoneVerificationActions } from '@/components/common/phone/PhoneVerificationActions';
import { PhoneVerificationBody } from '@/components/common/phone/PhoneVerificationBody';
import { SUCCESS_MESSAGE_TITLE_ID, SuccessMessageContent } from '@/components/common/SuccessMessageContent';
import { Theme } from '@mui/material/styles';
import { usePhoneVerificationFlow } from '@/components/common/phone/usePhoneVerificationFlow';
import React, { FC, ReactElement, ReactNode, useState } from 'react';
import type { PhoneBindingMode } from '@/types/user/PhoneBindingMode';
import type { PhoneVerificationFlow } from '@/components/common/phone/usePhoneVerificationFlow';

interface Props {
  open: boolean;
  mode: Exclude<PhoneBindingMode, 'NONE'>;
  username: string;
  isMobile?: boolean;
  onFlowChange: (isRunning: boolean) => void;
  onBound: () => void;
  onClose: () => void;
}

/**
 * Dialog asking the user to bind a confirmed Russian mobile number, and carrying the binding itself.
 *
 * @param props The dialog state, the mode to render, the login to show and the three exit handlers.
 */
export const PhoneBindingDialog: FC<Props> = (props: Props): ReactElement => {
  const { open, mode, username, isMobile, onFlowChange, onBound, onClose } = props;

  const flow: PhoneVerificationFlow = usePhoneVerificationFlow();
  const [isFlowStarted, setFlowStarted] = useState<boolean>(false);

  const isNotice: boolean = !isFlowStarted;
  const isSuccess: boolean = isFlowStarted && flow.step === 'success';
  const isForm: boolean = isFlowStarted && !isSuccess;
  const isBarrier: boolean = mode === 'REQUIRED';

  const handleBind = (): void => {
    setFlowStarted(true);
    onFlowChange(true);
  };

  const handleLeaveFlow = (): void => {
    flow.reset();
    setFlowStarted(false);
    onFlowChange(false);
  };

  const handleBack = (): void => {
    if (flow.goBack()) {
      return;
    }
    handleLeaveFlow();
  };

  const handleDone = (): void => {
    onBound();
    handleLeaveFlow();
  };

  const handleCloseButton = (): void => {
    if (isNotice) {
      onClose();
      return;
    }
    if (isSuccess) {
      handleDone();
      return;
    }
    handleBack();
  };

  const resolveLabel = (): string | undefined => {
    if (isSuccess) {
      return undefined;
    }
    if (isNotice) {
      return isBarrier ? undefined : 'Пожалуйста, привяжите номер мобильного телефона';
    }
    return 'Привязка номера телефона';
  };

  const label: string | undefined = resolveLabel();

  /**
   * Names the dialog on the two steps whose heading lives in the body rather than in the title row —
   * the barrier, which shows a lock icon above its heading, and the success screen.
   * Both leave the title row holding only the ✕, and MUI would otherwise point at that empty element.
   */
  const resolveTitleId = (): string | undefined => {
    if (isSuccess) {
      return SUCCESS_MESSAGE_TITLE_ID;
    }
    if (isNotice && isBarrier) {
      return PHONE_BINDING_NOTICE_TITLE_ID;
    }
    return undefined;
  };

  const renderBody = (): ReactNode => {
    if (isNotice) {
      return <PhoneBindingNotice isBarrier={isBarrier} isMobile={isMobile} onBind={handleBind} />;
    }
    if (isSuccess) {
      return (
        <SuccessMessageContent
          title={'Номер телефона привязан'}
          description={'Личный кабинет полностью доступен. Изменить номер можно в настройках профиля.'}
        />
      );
    }
    return <PhoneVerificationBody flow={flow} isAddPhone={true} isMobile={isMobile} username={username} />;
  };

  const renderActions = (): ReactNode => {
    if (isNotice) {
      return isMobile ? (
        <DialogSingleAction label={'Привязать номер'} isMobile={isMobile} onClick={handleBind} />
      ) : undefined;
    }
    if (isSuccess) {
      return <DialogSingleAction label={'Продолжить'} isMobile={isMobile} onClick={handleDone} />;
    }
    return <PhoneVerificationActions flow={flow} isMobile={isMobile} onBack={handleBack} />;
  };

  return (
    <ClientDialog
      open={open}
      isMobile={isMobile}
      fullWidth={true}
      maxWidth={'xs'}
      PaperProps={{
        sx: {
          minWidth: isMobile ? undefined : 'unset',
          minHeight: isMobile || isSuccess ? '0' : '420px',
        },
      }}
      label={label}
      sxTitle={label ? undefined : { paddingBottom: 0 }}
      aria-labelledby={resolveTitleId()}
      closeLabel={isForm ? 'Назад' : undefined}
      handleCloseButton={handleCloseButton}
      onClose={isBarrier ? undefined : handleCloseButton}
      sxContent={(theme: Theme) => ({
        paddingBottom: getDialogContentPaddingBottom(theme, isMobile, isSuccess),
        overflowY: isForm ? 'visible' : undefined,
      })}
      actions={renderActions()}>
      {renderBody()}
    </ClientDialog>
  );
};
